import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listJoinRequests, getOrgInsights, type JoinRequest, type OrgInsights } from '../lib/api';
import { hoursAgo } from '../lib/timeAgo';

export function DashboardPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [insights, setInsights] = useState<OrgInsights | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      listJoinRequests(user.orgId).catch(() => [] as JoinRequest[]),
      getOrgInsights(user.orgId).catch(() => null),
    ]).then(([reqs, ins]) => {
      setRequests(reqs);
      setInsights(ins);
      setLoading(false);
    });
  }, [user]);

  if (!user) return null;

  const canSeeApprovals = user.role === 'admin' || user.role === 'owner';
  const pendingCount = requests.length;
  const slaBreached = requests.filter((r) => hoursAgo(r.requestedAt) > 72);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{user.orgName}</p>
        </div>
      </div>

      {/* R-73: Approvals card — default landing when count > 0 */}
      {canSeeApprovals && !loading && pendingCount > 0 && (
        <div
          className="card"
          style={{
            marginBottom: 'var(--space-6)',
            borderLeft: '4px solid var(--color-accent)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 'var(--space-4)',
            }}
          >
            <div>
              <h2 style={{ fontSize: '1rem' }}>
                {pendingCount} join{' '}
                {pendingCount === 1 ? 'request' : 'requests'} pending
              </h2>
              {slaBreached.length > 0 && (
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-warning)',
                    marginTop: 'var(--space-1)',
                  }}
                >
                  ⚠ {slaBreached.length} waiting over 72 hours — action needed
                </p>
              )}
            </div>
            <Link to="/approvals" className="btn btn--primary btn--sm">
              Review approvals
            </Link>
          </div>
        </div>
      )}

      {/* Stat cards */}
      {insights && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 'var(--space-4)',
            marginBottom: 'var(--space-8)',
          }}
        >
          <StatCard
            label="Active members"
            value={insights.activeMembers.toLocaleString()}
          />
          <StatCard
            label="Pending approvals"
            value={insights.pendingApprovals.toLocaleString()}
            good={insights.pendingApprovals === 0}
          />
          <StatCard
            label="Coach messages (30d)"
            value={insights.coachMessages30d.toLocaleString()}
          />
        </div>
      )}

      {loading && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9375rem' }}>
          Loading…
        </p>
      )}

      {!loading && !insights && (
        <div className="empty-state">
          <p className="empty-state__title">No insights yet</p>
          <p className="empty-state__body">
            Insights appear once your team has been active for a few days.
          </p>
        </div>
      )}
    </>
  );
}

function StatCard({
  label,
  value,
  target,
  good,
}: {
  label: string;
  value: string;
  target?: string;
  good?: boolean;
}) {
  return (
    <div className="card">
      <p
        style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--color-text-muted)',
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: '2rem',
          fontWeight: 700,
          marginTop: 'var(--space-2)',
          lineHeight: 1,
        }}
      >
        {value}
      </p>
      {target !== undefined && (
        <p
          style={{
            fontSize: '0.8125rem',
            marginTop: 'var(--space-2)',
            color: good ? 'var(--color-success)' : 'var(--color-warning)',
          }}
        >
          {good ? '✓' : '·'} {target}
        </p>
      )}
    </div>
  );
}

